import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { MitigationStats, StatsResult } from "@/_models";
import { AircraftService, AssessmentService, UserService } from "@/_services";
import { from, Observable, Subscription } from "rxjs";
import {
    debounceTime,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    tap,
} from "rxjs/operators";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { PdfConfirmExportComponent } from "@/components/shared/modals/pdf-confirm-export/pdf-confirm-export.component";
import { LoadingModalComponent } from "@/components/shared/loading-modal/loading-modal.component";
import { ReportExportResultDto } from "@/_models/stats-export-response";
import { expandingFabAnimations } from "@/_utility/animations";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { FilterService } from "@/_services/filter.service";
import { getTimeFrameList } from "@/_models/filters/default-time-frame-list";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";

@Component({
    selector: "app-performance-legacy",
    templateUrl: "./performance-legacy.component.html",
    styleUrls: ["./performance-legacy.component.scss"],
    animations: [expandingFabAnimations],
})
export class PerformanceLegacyComponent implements OnInit, OnDestroy {
    stats$: Observable<StatsResult>;
    statsData$: Observable<MitigationStats>;
    filterForm: UntypedFormGroup;
    filterFormSubscription: Subscription;
    currentFilters;

    timeFrameList: RadioOption[] = getTimeFrameList(["All"]);
    userList$: Observable<RadioOption[]>;
    aircraftList$: Observable<RadioOption[]>;
    formList$: Observable<RadioOption[]>;

    constructor(
        private userService: UserService,
        private aircraftService: AircraftService,
        private modalService: NgbModal,
        private toastService: ToastrService,
        private formBuilder: UntypedFormBuilder,
        private assessmentService: AssessmentService,
        private filterService: FilterService,
    ) {}

    ngOnInit(): void {
        this.buildFilterForm();

        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => {
                    return { label: user.fullName, value: user._id };
                }),
            ),
            shareReplay(),
        );

        this.aircraftList$ = this.aircraftService.listAircraft().pipe(
            map((aircrafts) =>
                aircrafts.map((aircraft) => {
                    return { label: aircraft.designation, value: aircraft._id };
                }),
            ),
            shareReplay(),
        );

        this.formList$ = this.assessmentService
            .getAssessmentsByType("assessment")
            .pipe(
                map((assessments) => {
                    const list = [
                        { label: "All", value: "" },
                        ...assessments.map((a) => ({
                            label: a.title,
                            value: a._id,
                        })),
                    ];

                    return list;
                }),
                shareReplay(),
            );
    }

    ngOnDestroy(): void {
        if (this.filterFormSubscription) {
            this.filterFormSubscription.unsubscribe();
        }
    }

    buildFilterForm() {
        this.filterForm = this.formBuilder.group({
            responsiblePilot: [""],
            timeFrame: [""],
            flightDate: [[]],
            aircraft: [""],
            airport: [""],
            assessment: [""],
        });

        this.filterFormSubscription = this.filterForm.valueChanges
            .pipe(
                debounceTime(500),
                tap((changes) => {
                    this.currentFilters = { ...changes };
                    this.filterService.saveSimpleFilters(
                        LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
                        changes,
                    );
                }),
                tap(() => this.updateFlightList()),
            )
            .subscribe();

        const savedFilters = this.filterService.loadSimpleFilters(
            LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
        );

        if (savedFilters) {
            this.filterForm.patchValue(savedFilters, {
                emitEvent: false,
                onlySelf: true,
            });
            this.currentFilters = { ...savedFilters };
        }

        this.updateFlightList();
    }

    buildFilterQuery() {
        return this.filterService.buildFilterQuery(this.filterForm);
    }

    updateFlightList() {
        this.stats$ = this.userService
            .getUserStats(this.buildFilterQuery())
            .pipe(shareReplay());
        this.statsData$ = this.stats$.pipe(
            map((stats) => stats.data),
            shareReplay(),
        );
    }

    resetAssessmentFilter(): void {
        this.filterForm.patchValue({ assessment: "" });

        const stored = this.filterService.loadSimpleFilters(
            LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
        );

        if (stored) {
            delete stored.assessment;
            this.filterService.saveSimpleFilters(
                LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
                stored,
            );
            this.currentFilters = { ...stored };
        }

        this.updateFlightList();
    }
}
